<template>

    <b-card no-body class="page-card report-widget">

        <b-card-header class="mx-3 px-0">
            <div class="title">
                <i class="far fa-fw fa-calendar-day mr-2"></i>
                {{ trans.title }}
            </div>

            <div class="action-buttons">
                <a target="_blank" :href="getPdfLinkUrl()" class="btn btn-dark">
                    <i class="fal fa-fw fa-file-pdf mr-1"></i> <span class="hidden-sm-down">Export (PDF)</span>
                </a>
            </div>
        </b-card-header>

        <b-card-body>

            <form class="form-report mb-3">
                <div class="form-row">

                    <div class="col-xl-3 col-md-4 col-sm-6 mb-3 mb-sm-0">
                        <div class="select-date input-group">
                            <div class="input-group-prepend">
                                <label class="input-group-text" for="date-time"><i
                                    class="fas fa-calendar-alt"></i></label>
                            </div>

                            <datetime
                                id="date-time"
                                class="flatpickr"
                                v-model="selectedDayMutable"
                                :config="datePickerConfig"
                            />
                        </div>
                    </div>

                    <div class="col-xl-2 col-md-3 col-sm-6">
                        <div class="select-contract input-group">
                            <select class="custom-select" v-model="filters.type"
                                    @change="filterChanged('type', $event.target.value)">
                                <option value="all">{{ trans.contract_types_all }}</option>

                                <option v-for="(contract, index) in contractTypes" :value="contract.id" :key="index">
                                    {{ contract.shortcode }}
                                </option>

                            </select>
                        </div>
                    </div>

                    <div class="col mb-0 form-group">
                        <div class="input-group" style="justify-content: flex-end">
                            <button type="button" v-if="isFiltered()" class="btn btn-danger"
                                    @click="resetFilter(); filter()">
                                <i class="fa fa-trash-alt"></i> <span class="hidden-sm-down">{{
                                    trans.btn.delete_filter
                                }}</span>
                            </button>
                        </div>
                    </div>

                </div>
            </form>

            <div class="loading-box" v-if="loading">
                <div class="loading-inner"><i class="fa fa-spin fa-circle-notch text-secondary"></i></div>
            </div>

            <div class="table-responsive table-scrollable" v-if="!loading"
                 :style="{ height: getTableScrollableHeight(105) }" v-cloak>

                <table class="table table-hover">
                    <thead>
                    <tr>
                        <th v-for="column in this.columns" @click="sortBy(column.name, column.sortType)"
                            :class="{ 'sortable': sortColumn !== '', 'active': sortColumn === column.name }">
                            <div class="d-flex justify-content-start">
                                <span class="column-label">{{ trans.columns[column.transProperty] }}</span>
                                <span class="sorting-indicator">
                                        <i class="fad fa-fw fa-sort" v-if="sortColumn !== column.name"></i>
                                        <i class="fad fa-fw"
                                           v-if="column.name === sortColumn"
                                           :class="{ 'fa-sort-down': sortDirection === 'desc', 'fa-sort-up': sortDirection === 'asc' }"></i>
                                    </span>
                            </div>
                        </th>
                        <th></th>
                    </tr>
                    </thead>

                    <tbody>

                    <tr v-if="datalist.length == 0">
                        <td colspan="5" class="text-center text-muted py-5 bg-white"><em>{{ trans.empty_data_msg }}</em>
                        </td>
                    </tr>

                    <tr v-for="(item, key) in sortedData" :key="key">
                        <td>
                            <span :title="trans.badge.wage_personal_number"
                                  class="badge badge-light px-3">{{ item.wage_personal_number }}</span>
                        </td>
                        <td>
                            <h5>{{ item.full_name }}</h5>
                            <div class="contract-position">
                                <span :title="trans.badge.contract_type_short"
                                      class="badge badge-warning align-baseline contract-type">{{
                                        item.contract_type_shortcode
                                    }}</span>
                                <em>{{ item.position_name }}</em>
                            </div>
                        </td>
                        <td class="shift-plan">
                            <div v-for="(itv, key) in item.shift_plan_intervals" :key="key">
                                <template v-if="itv.plan_interval_type == workType">
                                  <span v-bind:key="itv.id">
                                    {{ itv.from }} - {{ itv.to }}
                                  </span>
                                    <span>
                                    {{ itv.length }}&nbsp;h.
                                  </span>
                                </template>

                                <template v-if="itv.plan_interval_type != workType">
                                    <div>
                                    <span>
                                      {{ translateWorkDayValue(itv.plan_interval_type) }}
                                    </span>
                                        <span>
                                      {{ itv.length }}&nbsp;h.
                                    </span>
                                    </div>
                                </template>
                            </div>
                        </td>
                        <td>
                            <div v-for="(itv, key) in item.intervals" :key="key" class="work-hours">
                                <template v-if="itv.work_interval_type == workType">
                                        <span class="badge badge-dark" v-bind:key="itv.id">
                                            {{ itv.from }} - {{ itv.to }}
                                        </span>
                                    <span class="badge badge-light">
                                            {{ itv.length }}&nbsp;h.
                                        </span>
                                </template>

                                <template v-if="itv.work_interval_type != workType">
                                    <div class="work-holiday">
                                            <span class="badge badge-holiday">
                                                {{ translateWorkDayValue(itv.work_interval_type) }}
                                            </span>
                                        <span class="badge badge-light">
                                                {{ itv.length }}&nbsp;h.
                                            </span>
                                    </div>
                                </template>
                            </div>
                        </td>
                        <td>
                            <div class="total-hours">
                                    <span class="badge badge-success">
                                        {{ item.length_total }}&nbsp;h.
                                    </span>

                                <span class="badge badge-midnight"
                                      v-if="item.night_shifts_hours && item.night_shifts_hours != '00:00'">
                                        <i class="fa fa-moon"></i>
                                        {{ item.night_shifts_hours }}
                                    </span>
                            </div>
                        </td>
                        <td>
                            <a :href="getAttendanceDetailUrl(item.employee_contract_id)" target="_blank"
                               :title="trans.btn.show_attendance" class="btn btn-spinner btn-primary">
                                <i class="far fa-fw fa-clock"></i>
                            </a>
                            <a :href="getEmployeeDetailUrl(item.employee_id)" target="_blank"
                               :title="trans.btn.show_employee" class="btn btn-spinner btn-success">
                                <i class="far fa-fw fa-user"></i>
                            </a>
                        </td>
                    </tr>

                    </tbody>

                </table>

            </div>

        </b-card-body>

    </b-card>

</template>

<script>
import AppListing from "../../../app-components/Listing/AppListing";

import { Slovak as DatePickerLocaleSk } from "flatpickr/dist/l10n/sk.js";
import { Czech as DatePickerLocaleCs } from "flatpickr/dist/l10n/cs.js";
import { sortData } from "../../../utils";
import moment from "moment";

const userLanguage = document.documentElement.lang;

export default {
    mixins: [AppListing],

    props: {
        'trans': {
            type: Object
        },
        'transWorkDay': {
            type: Object
        },
        'contractTypes': {
            type: Array,
            required: false,
            default() {
                return [];
            }
        },
        'url': {
            type: String
        },
        'baseUrl': {
            type: String
        },
        'selectedDay': {
            type: String
        },
        'workType': {
            type: String
        }
    },

    data() {
        return {
            filters: {
                type: 'all',
            },
            loading: true,
            initSetDataComplete: false,
            selectedDayMutable: '',
            datePickerConfig: {
                dateFormat: 'Y-m-d',
                altInput: true,
                altFormat: "d. F Y",
                locale: this.getDatePickerLocale(userLanguage)
            },
            datalist: [],
            sortColumn: 'full_name',
            sortDirection: 'asc',
            sortType: 'string',
            columns: [
                {
                    name: 'wage_personal_number',
                    sortType: 'string',
                    transProperty: 'wage_personal_number'
                },
                {
                    name: 'full_name',
                    sortType: 'string',
                    transProperty: 'employee'
                },
                {
                    name: 'shift_plan_intervals',
                    sortType: 'interval',
                    transProperty: 'shift_plan'
                },
                {
                    name: 'intervals',
                    sortType: 'interval',
                    transProperty: 'work_time'
                },
                {
                    name: 'length_total',
                    sortType: 'string',
                    transProperty: 'total_time'
                },
            ]
        };
    },

    mounted() {
        // load saved sorting state
        if (sessionStorage.getItem(this.sessionSortingKeyName) !== null) {
            let storedState = JSON.parse(sessionStorage.getItem(this.sessionSortingKeyName));
            if (storedState.sortingColumn && storedState.sortingDirection && storedState.sortingType) {
                this.sortColumn = storedState.sortingColumn;
                this.sortDirection = storedState.sortingDirection;
                this.sortType = storedState.sortingType;
            }
        }
    },

    created() {
        const instantLoad = (this.selectedDayMutable != '');
        this.selectedDayMutable = this.selectedDay;
        if (instantLoad)
            this.loadData();
    },

    methods: {
        loadData() {
            this.loading = true;

            let data = {
                params: {
                    type: this.filters.type
                }
            };

            axios.get(this.url, data)
                .then(response => {
                    this.datalist = response.data;
                    this.loading = false;
                })
                .catch(error => {
                });
        },

        emitParentUpdate() {
            if (window.history.replaceState) {
                window.history.replaceState({}, null, this.getFilterableUrl());
            }

            // emit event in parent and regenerate data in both child widgets by auto-update props
            this.$emit('updatedate', this.selectedDayMutable);
        },

        getFilterableUrl(withoutBase) {
            return withoutBase ? '' : this.baseUrl;
        },

        filterChanged(key, value) {
            this.filters[key] = value;
            this.$emit('filter', this.filters[key]);
            this.loadData();
        },

        getPdfLinkUrl() {
            return this.baseUrl + '/export?type=' + (this.filters.type ? this.filters.type : '') +
                '&sortColumn=' + (this.sortColumn ? this.sortColumn : '') +
                '&sortDirection=' + (this.sortDirection ? this.sortDirection : '') +
                '&sortType=' + (this.sortType ? this.sortType : '')
        },

        getDatePickerLocale(langCode) {
            if (langCode == 'sk') return DatePickerLocaleSk;
            if (langCode == 'cs') return DatePickerLocaleCs;
            return null;
        },

        getAttendanceDetailUrl(employeeContractId) {
            const dt = moment(this.selectedDayMutable, "YYYY-MM-DD");
            return '/attendance-detail/' + employeeContractId + '/' + dt.format("YYYY") + '/' + dt.format("MM");
        },

        getEmployeeDetailUrl(employeeId) {
            return '/employees/' + employeeId + '/show';
        },

        translateWorkDayValue(type) {
            var shortVal = "";
            var val = "";

            for (const [key, value] of Object.entries(this.transWorkDay)) {
                if (type + "_short" == key) shortVal = value;
                if (type == key) val = value;
            }
            return (shortVal ? shortVal : (val ? val : '?'));
        },

        intervalLength(start, end) {
            const startTime = moment(start);
            const endTime = moment(end);

            return moment.utc(endTime.diff(startTime)).format("HH:mm");
        },

        isFiltered() {
            if (this.filters.type != 'all') {
                return 1;
            } else {
                return 0;
            }
        },

        resetFilter() {
            this.filters.type = 'all';
            this.$emit('filter', this.filters.type);
            this.loadData();
        },

        sortBy(column, sortType) {
            if (this.sortColumn === column) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortDirection = 'asc';
            }

            this.sortColumn = column;
            this.sortType = sortType

            // keep saved state in session storage
            sessionStorage.setItem(this.sessionSortingKeyName, JSON.stringify({
                'sortingColumn': this.sortColumn,
                'sortingDirection': this.sortDirection,
                'sortingType': this.sortType,
            }));
        },
    },

    computed: {
        sessionSortingKeyName: {
            get() {
                return 'sortingState-daily-report';
            },
            set() {
            }
        },

        sortedData: {
            get() {
                return sortData(this.datalist, this.sortColumn, this.sortType, this.sortDirection);
            },
            set() {
            }
        },
    },

    watch: {
        selectedDayMutable: function (newVal, oldVal) {
            if (newVal != oldVal) {
                this.emitParentUpdate();
            }
        }

    }

}
</script>

<style scoped>
.sortable {
    user-select: none;
    cursor: pointer;
    overflow: hidden;

    .sorting-indicator {
        position: relative;
        padding-left: 0.5rem;

        .fad {
            color: #59698f;
            font-size: 0.6rem;
        }
    }

    .column-label {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .active {
        background-color: #d6def2 !important;

        .sorting-indicator .fad {
            color: #111;
        }
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/admin/js/report/daily-report/widgets/Report.vue